/**
 * AdminSSOPage — `/admin/sso` — in-app visibility into WorkOS provisioning
 * state. Three sections:
 *
 *  1. Organizations — every WorkOS Organization with its allowed email
 *     domains + a one-click "Generate setup portal link" that returns a
 *     short-lived WorkOS Admin Portal URL the customer's IT admin can follow
 *     to self-service configure their SAML/OIDC connection.
 *  2. Connections — SAML / OIDC / OAuth connections across all orgs with
 *     state badge (active / pending / failed).
 *  3. SSO Users — local KStreak users who have ever logged in via WorkOS,
 *     cross-referencing WorkOS profile IDs so you can debug "why isn't Alice
 *     getting matched?" without opening two dashboards.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Building2, ShieldCheck, Users, ExternalLink, Copy, Loader2, RefreshCw,
  Link2, AlertCircle, Plug, FileKey2, Plus, Globe, Clock,
} from 'lucide-react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const copyToClipboard = async (text) => {
  try { await navigator.clipboard.writeText(text); toast.success('Copied'); }
  catch { toast.error('Copy failed'); }
};

const stateBadge = (s) => {
  if (!s) return <Badge className="bg-[#9CA3AF]/15 text-[#6B7280] text-[9px]">—</Badge>;
  const lower = s.toLowerCase();
  const cls = lower === 'active' || lower === 'linked' ? 'bg-[#34C759]/15 text-[#34C759]'
    : lower.includes('draft') || lower.includes('validating') || lower.includes('pending') ? 'bg-[#FF9500]/15 text-[#FF9500]'
    : lower.includes('invalid') || lower.includes('inactive') ? 'bg-[#FF3B30]/15 text-[#FF3B30]'
    : 'bg-[#002FA7]/15 text-[#002FA7]';
  return <Badge className={`${cls} text-[9px]`} data-testid={`state-${lower}`}>{s}</Badge>;
};

const AdminSSOPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}` };

  const [loading, setLoading] = useState(true);
  const [orgs, setOrgs] = useState([]);
  const [connections, setConnections] = useState([]);
  const [directories, setDirectories] = useState([]);
  const [ssoUsers, setSsoUsers] = useState([]);
  const [enforcement, setEnforcement] = useState([]);  // list of enforced domains
  const [webhookEvents, setWebhookEvents] = useState([]);
  const [migrationRollup, setMigrationRollup] = useState(null);
  const [togglingDomain, setTogglingDomain] = useState(null);
  const [revokingOrg, setRevokingOrg] = useState(null);
  const [err, setErr] = useState(null);

  // Portal link dialog state
  const [portalOpen, setPortalOpen] = useState(false);
  const [portalOrg, setPortalOrg] = useState(null);
  const [portalIntent, setPortalIntent] = useState('sso');
  const [portalEmails, setPortalEmails] = useState('');
  const [portalLink, setPortalLink] = useState(null);
  const [generating, setGenerating] = useState(false);

  // Create Organization dialog state
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createDomains, setCreateDomains] = useState('');
  const [createAdminEmails, setCreateAdminEmails] = useState('');
  const [creating, setCreating] = useState(false);
  const [createdPortalLink, setCreatedPortalLink] = useState(null);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const [o, c, d, u, e, w, m] = await Promise.all([
        axios.get(`${API}/admin/sso/organizations`, { headers }),
        axios.get(`${API}/admin/sso/connections`, { headers }),
        axios.get(`${API}/admin/sso/directories`, { headers }),
        axios.get(`${API}/admin/sso/local-users`, { headers }),
        axios.get(`${API}/admin/sso/enforcement`, { headers }),
        axios.get(`${API}/admin/sso/webhook-events`, { headers }),
        axios.get(`${API}/admin/domain-migration/rollup`, { headers }).catch(() => ({ data: null })),
      ]);
      setOrgs(o.data.organizations || []);
      setConnections(c.data.connections || []);
      setDirectories(d.data.directories || []);
      setSsoUsers(u.data.users || []);
      setEnforcement(e.data.enforced || []);
      setWebhookEvents(w.data.events || []);
      setMigrationRollup(m.data || null);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load SSO data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (token) load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const openPortalDialog = (org, intent = 'sso') => {
    setPortalOrg(org);
    setPortalIntent(intent);
    setPortalEmails('');
    setPortalLink(null);
    setPortalOpen(true);
  };

  const generatePortalLink = async () => {
    if (!portalOrg) return;
    setGenerating(true);
    try {
      const admins = (portalEmails || '').split(/[,;\s]+/).map(s => s.trim()).filter(s => s.includes('@'));
      const r = await axios.post(
        `${API}/admin/sso/portal-link`,
        { organization_id: portalOrg.id, intent: portalIntent, admin_emails: admins.length ? admins : null },
        { headers },
      );
      setPortalLink(r.data.link);
      copyToClipboard(r.data.link);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not generate link');
    } finally {
      setGenerating(false);
    }
  };

  const countsForOrg = (orgId) => {
    const conns = connections.filter(c => c.organization_id === orgId);
    const dirs = directories.filter(d => d.organization_id === orgId);
    const active = conns.filter(c => (c.state || '').toLowerCase() === 'active').length;
    return { conns: conns.length, active, dirs: dirs.length };
  };

  const isEnforced = (domain) => enforcement.some(e => e.domain === domain);

  const toggleEnforcement = async (org, domain) => {
    const currentlyEnforced = isEnforced(domain);
    const next = !currentlyEnforced;
    // Safety prompt when enabling — catastrophic if misconfigured.
    if (next && !window.confirm(
      `Enforce SSO for @${domain}?\n\nAll users with emails at this domain will be REQUIRED to sign in via WorkOS. ` +
      `Password and Google login will return 403 for them.\n\nMake sure:\n  1. The organization has an active SAML/OIDC connection\n  2. Your existing users can actually authenticate via that IdP\n\nContinue?`
    )) return;
    setTogglingDomain(domain);
    try {
      await axios.post(
        `${API}/admin/sso/enforcement`,
        { organization_id: org.id, domain, enforced: next },
        { headers },
      );
      toast.success(next ? `SSO enforced for @${domain}` : `Enforcement removed for @${domain}`);
      // Refresh local state
      if (next) {
        setEnforcement([...enforcement, { domain, organization_id: org.id, enforced_at: new Date().toISOString() }]);
      } else {
        setEnforcement(enforcement.filter(e => e.domain !== domain));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not toggle enforcement');
    } finally {
      setTogglingDomain(null);
    }
  };

  const revokeOrgSessions = async (org) => {
    const affected = ssoUsers.filter(u => u.sso_organization_id === org.id).length;
    if (!window.confirm(
      `Revoke all active sessions for "${org.name}"?\n\n` +
      `This immediately invalidates every JWT held by the ${affected} KStreak user(s) linked to this organization. ` +
      `They'll be forced to log in again (via SSO if enforcement is on).\n\nUse for offboarding or security incidents.\n\nContinue?`
    )) return;
    setRevokingOrg(org.id);
    try {
      const r = await axios.post(
        `${API}/admin/sso/revoke-org-sessions`,
        { organization_id: org.id },
        { headers },
      );
      toast.success(`Revoked sessions for ${r.data.revoked_count} user(s)`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not revoke sessions');
    } finally {
      setRevokingOrg(null);
    }
  };

  const openCreateDialog = () => {
    setCreateName('');
    setCreateDomains('');
    setCreateAdminEmails('');
    setCreatedPortalLink(null);
    setCreateOpen(true);
  };

  const createOrganization = async () => {
    const name = (createName || '').trim();
    const domains = (createDomains || '').split(/[,;\s]+/).map(s => s.trim().toLowerCase()).filter(Boolean);
    const admins = (createAdminEmails || '').split(/[,;\s]+/).map(s => s.trim()).filter(s => s.includes('@'));
    if (!name) { toast.error('Organization name is required'); return; }
    if (!domains.length) { toast.error('Add at least one email domain'); return; }
    setCreating(true);
    try {
      const r = await axios.post(
        `${API}/admin/sso/organizations`,
        { name, domains, admin_emails: admins.length ? admins : null },
        { headers },
      );
      toast.success(`Organization "${r.data.organization.name}" created`);
      if (r.data.portal_link) {
        setCreatedPortalLink(r.data.portal_link);
        copyToClipboard(r.data.portal_link);
      }
      // Refresh the orgs list so the new row appears.
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create organization');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-sso-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-[#0A0A0A]">SSO Provisioning</h1>
          <p className="text-[#4B5563] text-sm">WorkOS Organizations, SAML/OIDC connections, and directory sync</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="refresh-sso-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
          Refresh
        </Button>
      </div>

      {err && (
        <div className="p-3 rounded-lg bg-[#FF3B30]/10 border border-[#FF3B30]/30 flex items-center gap-2 text-sm text-[#FF3B30]" data-testid="sso-error">
          <AlertCircle className="w-4 h-4" /> {err}
        </div>
      )}

      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border-[#E5E7EB]"><CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#002FA7]/10 flex items-center justify-center"><Building2 className="w-5 h-5 text-[#002FA7]" /></div>
          <div><p className="text-xl font-bold text-[#0A0A0A]">{orgs.length}</p><p className="text-xs text-[#9CA3AF]">Organizations</p></div>
        </CardContent></Card>
        <Card className="border-[#E5E7EB]"><CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#34C759]/10 flex items-center justify-center"><Plug className="w-5 h-5 text-[#34C759]" /></div>
          <div>
            <p className="text-xl font-bold text-[#0A0A0A]">{connections.filter(c => (c.state||'').toLowerCase()==='active').length} / {connections.length}</p>
            <p className="text-xs text-[#9CA3AF]">Connections (active / total)</p>
          </div>
        </CardContent></Card>
        <Card className="border-[#E5E7EB]"><CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#FF9500]/10 flex items-center justify-center"><FileKey2 className="w-5 h-5 text-[#FF9500]" /></div>
          <div><p className="text-xl font-bold text-[#0A0A0A]">{directories.length}</p><p className="text-xs text-[#9CA3AF]">Directories (SCIM)</p></div>
        </CardContent></Card>
        <Card className="border-[#E5E7EB]"><CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#AF52DE]/10 flex items-center justify-center"><Users className="w-5 h-5 text-[#AF52DE]" /></div>
          <div><p className="text-xl font-bold text-[#0A0A0A]">{ssoUsers.length}</p><p className="text-xs text-[#9CA3AF]">KStreak SSO users</p></div>
        </CardContent></Card>
      </div>

      {/* Domain Migration rollup */}
      {migrationRollup && migrationRollup.total > 0 && (
        <Card className="border-[#E5E7EB]" data-testid="migration-rollup-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Globe className="w-5 h-5 text-[#002FA7]" />
                Custom-domain migration status
                <Badge className="bg-[#002FA7]/10 text-[#002FA7] text-[10px] ml-1">{migrationRollup.total} tracked</Badge>
                {migrationRollup.stalled_count > 0 && (
                  <Badge className="bg-[#FF3B30]/15 text-[#FF3B30] text-[10px]" data-testid="stalled-badge">
                    <Clock className="w-3 h-3 mr-1" />{migrationRollup.stalled_count} stalled &gt;7d
                  </Badge>
                )}
              </CardTitle>
              <Button size="sm" variant="outline" onClick={() => navigate('/admin/domain-migration')} data-testid="open-domain-migration-btn">
                Open Domain Migration <ExternalLink className="w-3.5 h-3.5 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Phase KPI strip */}
            <div className="grid grid-cols-3 md:grid-cols-7 gap-2">
              {migrationRollup.phase_order.map((phase) => {
                const count = migrationRollup.by_phase[phase] || 0;
                const labels = {
                  pending: 'Pending',
                  verifying: 'Verifying',
                  dns_added: 'DNS added',
                  dns_propagated: 'DNS live',
                  tls_live: 'TLS live',
                  live: 'Reachable',
                  announced: 'Announced',
                };
                const colors = {
                  pending: 'bg-[#9CA3AF]/10 text-[#6B7280] border-[#E5E7EB]',
                  verifying: 'bg-[#9CA3AF]/15 text-[#6B7280] border-[#E5E7EB]',
                  dns_added: 'bg-[#FF9500]/10 text-[#FF9500] border-[#FF9500]/30',
                  dns_propagated: 'bg-[#FFC107]/15 text-[#B45309] border-[#FFC107]/40',
                  tls_live: 'bg-[#5AC8FA]/10 text-[#0369A1] border-[#5AC8FA]/40',
                  live: 'bg-[#34C759]/10 text-[#34C759] border-[#34C759]/30',
                  announced: 'bg-[#002FA7]/10 text-[#002FA7] border-[#002FA7]/30',
                };
                return (
                  <div key={phase} className={`rounded-lg border p-2 text-center ${colors[phase]}`} data-testid={`phase-${phase}`}>
                    <p className="text-lg font-bold">{count}</p>
                    <p className="text-[10px] uppercase tracking-wide">{labels[phase]}</p>
                  </div>
                );
              })}
            </div>
            {/* Top 5 furthest-from-complete domains */}
            {migrationRollup.domains.length > 0 && (
              <div className="pt-1">
                <p className="text-[11px] uppercase tracking-wide text-[#9CA3AF] mb-1">Most behind (lowest progress first)</p>
                <div className="space-y-1">
                  {migrationRollup.domains.slice(0, 5).map((d) => (
                    <div key={d.domain} className="flex items-center gap-3 text-xs py-1" data-testid={`rollup-row-${d.domain}`}>
                      <code className="text-[#111827] font-mono flex-1 truncate">{d.domain}</code>
                      <div className="w-24 h-1.5 bg-[#F3F4F6] rounded-full overflow-hidden">
                        <div className="h-full bg-[#002FA7]" style={{ width: `${d.progress}%` }} />
                      </div>
                      <span className="text-[#6B7280] w-10 text-right">{d.progress}%</span>
                      <Badge className="bg-[#F3F4F6] text-[#4B5563] text-[9px]">{d.phase.replace('_', ' ')}</Badge>
                      {d.stalled && <Badge className="bg-[#FF3B30]/15 text-[#FF3B30] text-[9px]">stalled</Badge>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Organizations */}
      <Card className="border-[#E5E7EB]" data-testid="orgs-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-base flex items-center gap-2"><Building2 className="w-5 h-5 text-[#002FA7]" />Organizations</CardTitle>
            <Button size="sm" className="bg-[#002FA7] hover:bg-[#00227A]" onClick={openCreateDialog} data-testid="create-org-btn">
              <Plus className="w-3.5 h-3.5 mr-1" />Create organization
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin text-[#9CA3AF] mx-auto" /></div>
          ) : orgs.length === 0 ? (
            <p className="p-4 text-xs text-[#9CA3AF]">No organizations provisioned yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB] text-left">
                  <tr>
                    <th className="px-4 py-2 text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">Name</th>
                    <th className="px-4 py-2 text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">Domains</th>
                    <th className="px-4 py-2 text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold text-center">Connections</th>
                    <th className="px-4 py-2 text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold text-center">Directories</th>
                    <th className="px-4 py-2 text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">Created</th>
                    <th className="px-4 py-2 text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orgs.map(org => {
                    const counts = countsForOrg(org.id);
                    return (
                      <tr key={org.id} className="border-b border-[#E5E7EB] hover:bg-[#F9FAFB]" data-testid={`org-row-${org.id}`}>
                        <td className="px-4 py-2">
                          <p className="font-medium text-[#0A0A0A]">{org.name}</p>
                          <p className="text-[10px] text-[#9CA3AF] font-mono">{org.id}</p>
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex flex-wrap gap-1.5">
                            {org.domains.map(d => {
                              const enforced = isEnforced(d);
                              return (
                                <div key={d} className="inline-flex items-center gap-1" data-testid={`domain-${d}`}>
                                  <Badge variant="outline" className="text-[9px]">@{d}</Badge>
                                  <button
                                    type="button"
                                    onClick={() => toggleEnforcement(org, d)}
                                    disabled={togglingDomain === d}
                                    className={`text-[8px] px-1.5 py-0.5 rounded-full border font-semibold transition-colors ${
                                      enforced
                                        ? 'bg-[#FF3B30]/10 border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/20'
                                        : 'bg-[#F3F4F6] border-[#E5E7EB] text-[#9CA3AF] hover:bg-[#E5E7EB]'
                                    } disabled:opacity-50`}
                                    title={enforced ? 'SSO is enforced — click to disable' : 'Click to enforce SSO for this domain'}
                                    data-testid={`enforce-toggle-${d}`}
                                  >
                                    {togglingDomain === d ? '…' : (enforced ? 'SSO ENFORCED' : 'enforce')}
                                  </button>
                                </div>
                              );
                            })}
                            {org.domains.length === 0 && <span className="text-[10px] text-[#9CA3AF]">—</span>}
                          </div>
                        </td>
                        <td className="px-4 py-2 text-center">
                          {counts.conns === 0 ? (
                            <span className="text-[#9CA3AF]">0</span>
                          ) : (
                            <span className={counts.active > 0 ? 'text-[#34C759] font-semibold' : 'text-[#FF9500] font-semibold'}>
                              {counts.active}/{counts.conns}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-center">{counts.dirs}</td>
                        <td className="px-4 py-2 text-[#6B7280] text-[11px]">
                          {org.created_at ? format(new Date(org.created_at), 'MMM d, yyyy') : '—'}
                        </td>
                        <td className="px-4 py-2 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button size="sm" variant="outline" className="h-7 text-[10px] text-[#002FA7] border-[#002FA7]/30" onClick={() => openPortalDialog(org)} data-testid={`portal-btn-${org.id}`}>
                              <Link2 className="w-3 h-3 mr-1" />Setup link
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-[10px] text-[#FF3B30] border-[#FF3B30]/30 hover:bg-[#FF3B30]/5"
                              onClick={() => revokeOrgSessions(org)}
                              disabled={revokingOrg === org.id}
                              title="Immediately invalidate every JWT held by users in this organization"
                              data-testid={`revoke-org-${org.id}`}
                            >
                              {revokingOrg === org.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <AlertCircle className="w-3 h-3 mr-1" />}
                              Revoke
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Connections */}
      <Card className="border-[#E5E7EB]" data-testid="connections-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><Plug className="w-5 h-5 text-[#34C759]" />SSO Connections</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {connections.length === 0 ? (
            <p className="p-4 text-xs text-[#9CA3AF]">No SSO connections yet. Generate a setup portal link above to invite a customer's IT admin to configure one.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB] text-left">
                  <tr>
                    <th className="px-4 py-2 text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">Name</th>
                    <th className="px-4 py-2 text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">Type</th>
                    <th className="px-4 py-2 text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">Organization</th>
                    <th className="px-4 py-2 text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">State</th>
                    <th className="px-4 py-2 text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {connections.map(c => {
                    const org = orgs.find(o => o.id === c.organization_id);
                    return (
                      <tr key={c.id} className="border-b border-[#E5E7EB]" data-testid={`conn-row-${c.id}`}>
                        <td className="px-4 py-2">
                          <p className="font-medium text-[#0A0A0A]">{c.name}</p>
                          <p className="text-[10px] text-[#9CA3AF] font-mono">{c.id}</p>
                        </td>
                        <td className="px-4 py-2 text-[#4B5563]">{c.connection_type}</td>
                        <td className="px-4 py-2 text-[#4B5563]">{org?.name || c.organization_id}</td>
                        <td className="px-4 py-2">{stateBadge(c.state)}</td>
                        <td className="px-4 py-2 text-[#6B7280] text-[11px]">{c.created_at ? format(new Date(c.created_at), 'MMM d, yyyy') : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* SSO Users */}
      <Card className="border-[#E5E7EB]" data-testid="sso-users-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><Users className="w-5 h-5 text-[#AF52DE]" />KStreak users logged in via SSO</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {ssoUsers.length === 0 ? (
            <p className="p-4 text-xs text-[#9CA3AF]">No SSO logins recorded yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB] text-left">
                  <tr>
                    <th className="px-4 py-2 text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">User</th>
                    <th className="px-4 py-2 text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">Role</th>
                    <th className="px-4 py-2 text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">WorkOS Profile</th>
                    <th className="px-4 py-2 text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">Connection</th>
                    <th className="px-4 py-2 text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">Last SSO Login</th>
                  </tr>
                </thead>
                <tbody>
                  {ssoUsers.map(u => (
                    <tr key={u.email} className="border-b border-[#E5E7EB]" data-testid={`sso-user-${u.email}`}>
                      <td className="px-4 py-2">
                        <p className="font-medium text-[#0A0A0A]">{u.name}</p>
                        <p className="text-[10px] text-[#9CA3AF]">{u.email}</p>
                      </td>
                      <td className="px-4 py-2"><Badge className="bg-[#002FA7]/10 text-[#002FA7] text-[9px]">{u.role}</Badge></td>
                      <td className="px-4 py-2 text-[10px] text-[#6B7280] font-mono">{u.sso_workos_user_id || '—'}</td>
                      <td className="px-4 py-2 text-[10px] text-[#6B7280] font-mono">{u.sso_connection_id || '—'}</td>
                      <td className="px-4 py-2 text-[#6B7280] text-[11px]">
                        {u.sso_last_login_at ? format(new Date(u.sso_last_login_at), 'MMM d, yyyy h:mm a') : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* WorkOS Webhook Events audit log */}
      <Card className="border-[#E5E7EB]" data-testid="webhook-events-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-base flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-[#34C759]" />WorkOS Webhook Events
              <Badge variant="outline" className="text-[9px] ml-1">auto-revoke on deprovision</Badge>
            </CardTitle>
            <p className="text-[10px] text-[#9CA3AF]">
              Endpoint URL for WorkOS Dashboard: <code className="font-mono text-[#4B5563]">/api/auth/sso/webhook</code>
            </p>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {webhookEvents.length === 0 ? (
            <p className="p-4 text-xs text-[#9CA3AF]">
              No webhook events received yet. Configure the endpoint on the WorkOS dashboard (Webhooks → Add endpoint) and subscribe to <code className="font-mono text-[10px]">connection.deleted</code>, <code className="font-mono text-[10px]">dsync.user.deleted</code>, and <code className="font-mono text-[10px]">dsync.user.updated</code>. Paste the Signing Secret into <code className="font-mono text-[10px]">WORKOS_WEBHOOK_SECRET</code>.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB] text-left">
                  <tr>
                    <th className="px-4 py-2 text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">Received</th>
                    <th className="px-4 py-2 text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">Event type</th>
                    <th className="px-4 py-2 text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">WorkOS event ID</th>
                    <th className="px-4 py-2 text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold text-right">Users revoked</th>
                  </tr>
                </thead>
                <tbody>
                  {webhookEvents.map((we, i) => (
                    <tr key={i} className="border-b border-[#E5E7EB]" data-testid={`webhook-row-${i}`}>
                      <td className="px-4 py-2 text-[#6B7280] text-[11px]">{we.received_at ? format(new Date(we.received_at), 'MMM d, h:mm:ss a') : '—'}</td>
                      <td className="px-4 py-2 font-mono text-[11px] text-[#0A0A0A]">{we.event_type || '—'}</td>
                      <td className="px-4 py-2 font-mono text-[10px] text-[#9CA3AF]">{we.workos_event_id || '—'}</td>
                      <td className="px-4 py-2 text-right">
                        {we.revoked_count > 0 ? (
                          <Badge className="bg-[#FF9500]/15 text-[#FF9500] text-[9px]">{we.revoked_count}</Badge>
                        ) : (
                          <span className="text-[10px] text-[#9CA3AF]">0</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Organization Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg" data-testid="create-org-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#002FA7]" />Create Organization
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-[#4B5563]">
              Provision a new WorkOS Organization for an enterprise customer. If you provide IT admin emails, WorkOS will email them a setup portal link directly so they can configure their SAML/OIDC connection.
            </p>
            <div>
              <Label className="text-xs font-semibold">Company name *</Label>
              <Input
                placeholder="Acme Corporation"
                value={createName}
                onChange={e => setCreateName(e.target.value)}
                className="text-sm mt-1"
                disabled={creating}
                data-testid="create-org-name-input"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Email domains *</Label>
              <Input
                placeholder="acme.com, acme.co.uk"
                value={createDomains}
                onChange={e => setCreateDomains(e.target.value)}
                className="text-sm mt-1"
                disabled={creating}
                data-testid="create-org-domains-input"
              />
              <p className="text-[10px] text-[#9CA3AF] mt-1">Comma-separated. Employees with these work email domains will land on this organization's SSO connection.</p>
            </div>
            <div>
              <Label className="text-xs font-semibold">IT admin emails (optional)</Label>
              <Input
                placeholder="itadmin@acme.com, sso@acme.com"
                value={createAdminEmails}
                onChange={e => setCreateAdminEmails(e.target.value)}
                className="text-sm mt-1"
                disabled={creating}
                data-testid="create-org-admins-input"
              />
              <p className="text-[10px] text-[#9CA3AF] mt-1">If provided, WorkOS auto-emails the setup portal link to these addresses.</p>
            </div>
            {createdPortalLink && (
              <div className="p-3 bg-[#F0FDF4] border border-[#34C759]/30 rounded-md space-y-2" data-testid="created-org-portal-link">
                <p className="text-[11px] text-[#166534] font-semibold">Organization created · Setup portal link generated</p>
                <div className="flex items-center gap-2">
                  <Input readOnly value={createdPortalLink} className="text-[10px] font-mono bg-white" data-testid="created-org-portal-url" />
                  <Button size="sm" variant="outline" onClick={() => copyToClipboard(createdPortalLink)}><Copy className="w-3 h-3" /></Button>
                  <Button size="sm" variant="outline" onClick={() => window.open(createdPortalLink, '_blank')} data-testid="open-created-portal-link"><ExternalLink className="w-3 h-3" /></Button>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Close</Button>
            <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={createOrganization} disabled={creating || createdPortalLink} data-testid="submit-create-org-btn">
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
              {createdPortalLink ? 'Created' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Portal Link Dialog */}
      <Dialog open={portalOpen} onOpenChange={setPortalOpen}>
        <DialogContent className="max-w-lg" data-testid="portal-link-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-[#002FA7]" />Generate Setup Portal Link
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {portalOrg && (
              <div className="p-3 rounded-lg bg-[#F9FAFB] border border-[#E5E7EB]">
                <p className="text-[11px] text-[#6B7280] uppercase tracking-wider">Organization</p>
                <p className="text-sm font-medium text-[#0A0A0A]">{portalOrg.name}</p>
                <p className="text-[10px] text-[#9CA3AF] font-mono">{portalOrg.id}</p>
              </div>
            )}
            <div>
              <Label className="text-xs font-semibold">Intent</Label>
              <div className="grid grid-cols-4 gap-1.5 mt-1" data-testid="portal-intent-group">
                {[
                  { v: 'sso', l: 'SSO' },
                  { v: 'dsync', l: 'SCIM' },
                  { v: 'audit_logs', l: 'Audit logs' },
                  { v: 'domain_verification', l: 'Domains' },
                ].map(o => (
                  <button
                    key={o.v}
                    type="button"
                    onClick={() => setPortalIntent(o.v)}
                    className={`h-8 rounded-md border text-[11px] font-medium transition-colors ${
                      portalIntent === o.v ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'border-[#E5E7EB] text-[#4B5563] hover:border-[#002FA7]'
                    }`}
                    data-testid={`portal-intent-${o.v}`}
                  >
                    {o.l}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold">Invite IT admins (optional)</Label>
              <Input
                placeholder="admin@customer.com, other@customer.com"
                value={portalEmails}
                onChange={e => setPortalEmails(e.target.value)}
                className="text-sm mt-1"
                data-testid="portal-admin-emails-input"
              />
              <p className="text-[10px] text-[#9CA3AF] mt-1">WorkOS will email them the setup link directly. Up to 20 emails.</p>
            </div>
            {portalLink && (
              <div className="p-3 bg-[#F0FDF4] border border-[#34C759]/30 rounded-md space-y-2" data-testid="generated-portal-link">
                <p className="text-[11px] text-[#166534] font-semibold">Link generated (valid for 5 min, copied to clipboard)</p>
                <div className="flex items-center gap-2">
                  <Input readOnly value={portalLink} className="text-[10px] font-mono bg-white" data-testid="portal-link-url" />
                  <Button size="sm" variant="outline" onClick={() => copyToClipboard(portalLink)}><Copy className="w-3 h-3" /></Button>
                  <Button size="sm" variant="outline" onClick={() => window.open(portalLink, '_blank')} data-testid="open-portal-link-btn"><ExternalLink className="w-3 h-3" /></Button>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPortalOpen(false)}>Close</Button>
            <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={generatePortalLink} disabled={generating} data-testid="generate-portal-link-btn">
              {generating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Link2 className="w-4 h-4 mr-1" />}
              Generate link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSSOPage;
